import * as React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { classNames } from '@features/utils/reactFunctions';
import * as elements from '@appStyles/_elements.module.scss';
import Dropzone, { ImageFile } from 'react-dropzone';
import Input from '@appComponents/Input/Input';
import CelsiusClaimService from '@services/CelsiusClaim.service';
import { SelectedFile } from './components/SelectedFile/SelectedFile';
import { CelsiusClaimStatus } from '../../CelsiusClaimPage';

import * as componentStyles from './RequiredSnapshotStateStyles.module.scss';
import * as style from '../../CelsiusClaimPageStyles.module.scss'

const MAX_FILE_SIZE = 2097152;
const SUPPORTED_FORMATS = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/svg',
  'image/gif',
  'image/jpg',
];

type Props = {
  setClaimStatus: React.Dispatch<React.SetStateAction<CelsiusClaimStatus>>;
};

export const RequiredSnapshotState = ({ setClaimStatus }: Props) => {
  const initialValues: { files: ImageFile[], apiKey: string } = {
    files: [],
    apiKey: '',
  };

  const formik = useFormik({
    initialValues,
    onSubmit: async (values) => {
      const allFiles = await Promise.all(values.files.map(file => toBase64(file)));
      CelsiusClaimService.uploadScreenshot(allFiles, values.apiKey)
        .then((resp) => setClaimStatus(resp.data.status))
        .catch((err) => console.log('err', err));
    },
    validationSchema: Yup.object().shape({
      files: Yup.array()
        .required()
        .test('fileSize', 'The file is too big, please upload a screenshot of less than 2MB', (values) => {
          const allSizesCorrect =
            values && values?.map((file) => file.size <= MAX_FILE_SIZE);

          return allSizesCorrect?.every((value) => value) || false;
        })
        .test('fileType', 'Unsupported File Format', (values) => {
          const allSizesCorrect =
            values &&
            values?.map((file) => SUPPORTED_FORMATS.includes(file?.type));

          return allSizesCorrect?.every((value) => value) || false;
        }),
    }),
    validateOnMount: true,
    enableReinitialize: true,
  });

  const toBase64 = (file: ImageFile) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const onDrop = async (files: ImageFile[]) => {
    formik.setFieldValue('files', formik.values.files.concat(files));
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue('apiKey', e.target.value);
  };

  const removeSelectedFile = (index: number) => {
    const updatedFiles = formik.values.files.filter((formikFile, formikIndex) => formikIndex !== index);
    formik.setFieldValue('files', updatedFiles);
  };

  return (
    <div className={style.headerWrapperPadding}>
      <h1 className={style.headerTitle}>Additional information is needed</h1>
      <p className={style.headerText}>
        We need to ask you to provide a proof of your Celsius balance. Please
        login into your Celsius app, make a screenshot of your current balance
        and upload it here
      </p>
      <div className={componentStyles.dropzoneWrapper}>
        <Dropzone
          activeClassName={componentStyles.dropzoneActive}
          multiple={true}
          onDrop={(file) => onDrop(file)}
          accept={SUPPORTED_FORMATS.join(',')}
        >
          <div className={componentStyles.snapshotRequired}>
            <div className={componentStyles.snapshotRequiredIcon} />

            <span>Drop file here, or browse</span>
            <br />
            <span className={style.headerText}>(JPG, PNG, PDF, max 2MB)</span>
          </div>
        </Dropzone>
      </div>

      <p className={style.headerText}>
        As an alternative, you can also provide your read only API key from Celsius to confirm your Celsius Balance
      </p>
      <div className={componentStyles.apiKeyWrapper}>
        <Input
          onChange={onChange}
        />
      </div>

      {formik.values.files.map((file, index) => (
        <SelectedFile
          file={file}
          key={`${file.name}-${index}`}
          index={index}
          removeSelectedFile={removeSelectedFile}
          isLoading={formik.isSubmitting}
        />
      ))}
      {formik.errors.files && (
        <p className={componentStyles.error}>Error: {formik.errors.files}</p>
      )}

      <button
        disabled={!formik.isValid || !formik.dirty || formik.isSubmitting}
        className={classNames(elements.btn, style.actionButton)}
        onClick={() => formik.handleSubmit()}
      >
        Submit
      </button>
    </div>
  );
};
